import React, { useEffect, useState, useContext } from 'react';
import Swal from 'sweetalert2';
import { AuthContext } from '../contexts/auth';
import { getEmployeePermissions, addEmployeePermission, removeEmployeePermission, getPermissions, getPermissionsDetailed } from '../services/api_permissions';
import Fetching from './fetching';
import PermissionsEmployeeTable from './permessi_employee_table';
import EmployeeRoleUnits from './employee_role_unit';

const RuoloTabEmployee = ({ employeeId }) => {
    const { token } = useContext(AuthContext);
    const [permissions, setPermissions] = useState([]);
    const [handlerPermissions, setHandlerPermissions] = useState([]);
    const [employeePermissions, setEmployeePermissions] = useState([]);
    const [permissionsDetailed, setPermissionsDetailed] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCompleteText, setShowCompleteText] = useState(false);

    // Carica permessi assegnati all'employee
    const fetchEmployeePermissions = async () => {
        try {
            const activePermissions = await getEmployeePermissions(token, employeeId);
            const activePermissionCodenames = activePermissions.map(p => p.codename);
            setPermissions(activePermissionCodenames);
        } catch (error) {
            console.error('Errore nel recuperare i permessi attivi:', error);
        }
    };

    const fetchActivePermissionsDetailed = async (showLoader = true) => {
        if (showLoader) setLoading(true);
        try {
            const permissions = await getPermissionsDetailed(token, employeeId);        
            setPermissionsDetailed(permissions);
        } catch (error) {
            console.error("Errore durante il recupero dei permessi attivi:", error);
        } finally {
            if (showLoader) setLoading(false);
        }
    };

    useEffect(() => {
        fetchEmployeePermissions();
    }, [token, employeeId]);

    useEffect(() => {
        fetchActivePermissionsDetailed();
    }, [token, employeeId]);

    // Carica tutti i permessi disponibili
    useEffect(() => {
        const fetchAllPermissions = async () => {
            setLoading(true);
        try {
            const allPermissions = await getPermissions(token);
            const handlerPerms = allPermissions.filter(p => p.role === 'handler');
            const employeePerms = allPermissions.filter(p => p.role === 'employee');

            setHandlerPermissions(handlerPerms);
            setEmployeePermissions(employeePerms);
        } catch (error) {
            console.error('Errore nel recupero dei permessi:', error);
        } finally {
            setLoading(false);
        }
        };

        fetchAllPermissions();
    }, [token]);

    const handlePermissionChange = async (permissionCodename, isChecked) => {      
        try {
            const allPermissions = [...handlerPermissions, ...employeePermissions];
            const permission = allPermissions.find(p => p.id === permissionCodename);
            if (!permission) throw new Error('Permesso non trovato');

            if (isChecked) {
                await addEmployeePermission(token, employeeId, permissionCodename);
            } else {
                await removeEmployeePermission(token, employeeId, permissionCodename);
            }
            
            await fetchEmployeePermissions(false);
            await fetchActivePermissionsDetailed(false);
        } catch (error) {
            console.error('Errore nell\'aggiornare il permesso:', error);
        
            let errorMessage = 'Errore nel salvataggio.';

            if (error.response && error.response.data) {
                const errorData = error.response.data;
                const errorMessages = [];

                const extractMessages = (value) => {
                    if (typeof value === 'string') return [value];
                    if (Array.isArray(value)) {
                        return value.flatMap(v => extractMessages(v));
                    }
                    if (typeof value === 'object' && value !== null) {
                        return Object.values(value).flatMap(v => extractMessages(v));
                    }
                    return [];
                };

                errorMessages.push(...extractMessages(errorData));

                if (errorMessages.length > 0) {
                    errorMessage = errorMessages.join(' ');
                }
            }
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: errorMessage,
                confirmButtonText: 'OK',
            });
        }
    };


    return (
        <div className="container p-6 w-full overflow-y-auto" style={{ height: '100vh' }}>
            <div className='mb-8'>
                <p className='text-sm text-justify'>
                    Ogni dipendente può disporre di permessi che gli permettono di svolgere determinate azioni all'interno della piattaforma, sia per il ruolo "Gestore" che ruolo "Dipendente".  
                </p>
                <div
                    onClick={() => setShowCompleteText(prev => !prev)}
                    className="flex items-center justify-center gap-1 text-indigo-800 hover:text-indigo-600 cursor-pointer text-sm font-medium transition-all mt-2"
                >
                <span
                    className={`transform transition-transform duration-300 ${
                    showCompleteText ? 'rotate-180' : 'rotate-0'
                    }`}
                >
                    ↓
                </span>
                    {showCompleteText ? 'Nascondi dettagli' : 'Continua a leggere'}
                </div>

                {showCompleteText && (
                    <>
                        <ul className='text-sm list-disc pl-6 mt-2 marker:text-indigo-500'>
                            <li>Permesso sul Dipendente: attivo solo per lui, ha priorità più alta.</li>
                            <li>Permesso sull’Unità Organizzativa (UO): ereditato se il dipendente appartiene a quella UO.</li>
                            <li>Permesso Aziendale: ereditato da tutta l'azienda.</li>
                        </ul>
                        <p className='text-sm mt-2'>
                            Se un permesso è attivo su più livelli, vale il livello con priorità più alta. L’ordine di priorità è:
                        </p>
                        <ul className='text-sm list-decimal pl-6'>
                            <li>Dipendente</li>
                            <li>Unità Organizzativa</li>
                            <li>Azienda</li>
                        </ul>
                    </>
                )}
            </div>

            <EmployeeRoleUnits employeeId={employeeId} />

            {loading ? (
                <Fetching />
            ) : (
                <>
                    <PermissionsEmployeeTable
                        title="GESTORE"
                        permissions={handlerPermissions}
                        activePermissions={permissions}
                        permissionsDetailed={permissionsDetailed}
                        onToggle={handlePermissionChange}
                        mode="edit"
                        prefix="handler"
                    />

                    <PermissionsEmployeeTable
                        title="DIPENDENTE"
                        permissions={employeePermissions}
                        activePermissions={permissions}
                        permissionsDetailed={permissionsDetailed}
                        onToggle={handlePermissionChange}
                        mode="edit"
                        prefix="employee"
                    />
                </>
            )}
        </div>
    );
};

export default RuoloTabEmployee;
